import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Observable, from, map, of } from 'rxjs';
import { Specialist } from '../models/specialist.model';
import { convertSnaps } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SpecialistService {
  constructor(private db: AngularFireDatabase) {}

  findById(specialistId: string): Observable<Specialist> {
    return this.db
      .object<Specialist>(`${localStorage.getItem('appId')}_specialists/${specialistId}`)
      .snapshotChanges()
      .pipe(map((snapshot) => convertSnaps<Specialist>([snapshot])[0]));
  }

  findSpecialists(): Observable<Specialist[]> {
    return this.db
      .list(`${localStorage.getItem('appId')}_specialists`)
      .snapshotChanges()
      .pipe(
        map((results) => convertSnaps<Specialist>(results)),
        map((specialists) => {
          return specialists.sort((a, b) => a.name.localeCompare(b.name));
        }),
      );
  }

  findSpecialistsByKeys(keys: string[]): Observable<Specialist[]> {
    if (!keys || keys.length === 0) {
      return of([]);
    }

    return this.db
      .list(`${localStorage.getItem('appId')}_specialists`)
      .snapshotChanges()
      .pipe(
        map((results) => convertSnaps<Specialist>(results)),
        map((specialists) => specialists.filter((specialist) => keys.includes(specialist.id))),
        map((users) => {
          return users.sort((a, b) => a.name.localeCompare(b.name));
        }),
      );
  }

  createSpeciality(specialist: Partial<Specialist>, specialityId?: string) {
    let save$: Observable<any>;

    if (specialityId) {
      save$ = from(
        this.db
          .object(`${localStorage.getItem('appId')}_specialists/${specialityId}`)
          .set(specialist),
      );
    } else {
      save$ = from(this.db.list(`${localStorage.getItem('appId')}_specialists`).push(specialist));
    }

    return save$.pipe(
      map((res) => {
        const newAppId = specialityId ?? res.key;
        return {
          id: newAppId,
          ...specialist,
        };
      }),
    );
  }

  update(specialityId: string, changes: Partial<Specialist>): Observable<void> {
    return from(
      this.db
        .object(`${localStorage.getItem('appId')}_specialists/${specialityId}`)
        .update(changes),
    );
  }

  async deleteSpecialist(specialist: Specialist) {
    const appId = localStorage.getItem('appId');
    const today = new Date().getTime();

    const visitsRef = this.db.database.ref(`${appId}_visits`);
    const specialistsRef = this.db.database.ref(`${appId}_specialists`);

    const visitSnapshot = await visitsRef.once('value');

    const visitDeletionPromises = [];

    visitSnapshot.forEach((visitChildSnapshot) => {
      const visitData = visitChildSnapshot.val();
      if (visitData.date < today && visitData.specialistId === specialist.id) {
        visitDeletionPromises.push(visitChildSnapshot.ref.remove());
      }
    });

    await Promise.all(visitDeletionPromises);
    await specialistsRef.child(specialist.id).remove();
  }
}
